import { useCallback, useEffect, useState } from "react";
import { useFeatureFlag } from "@/feature-flags/useFeatureFlag";
import { FeatureFlag } from "@/feature-flags/featureFlags";
import { usePreferenceStorage } from "@/setup/frontend/storage/PreferenceStorageContext";

export const ONBOARDING_COMPLETED_KEY = "vcg-caregiver-onboarding-completed";
export const ONBOARDING_PENDING_KEY = "vcg-caregiver-onboarding-pending";

export type OnboardingPendingPayload = {
	notificationsPermission: "granted" | "denied" | "not-determined";
	locationPermission: "granted" | "denied" | "not-determined";
	microphonePermission: "granted" | "denied" | "not-determined";
	smsConsent: boolean;
};

export function useOnboardingCompleted() {
	const prefs = usePreferenceStorage();
	const onboardingEnabled = useFeatureFlag(FeatureFlag.CaregiverAppOnboarding);
	const [completedLocally, setCompletedLocally] = useState<boolean | null>(
		null,
	);

	useEffect(() => {
		if (!onboardingEnabled) {
			setCompletedLocally(true);
			return;
		}
		prefs
			.getItem(ONBOARDING_COMPLETED_KEY)
			.then((value) => setCompletedLocally(value === "true"))
			.catch(() => setCompletedLocally(false));
	}, [prefs, onboardingEnabled]);

	const markCompleted = useCallback(async () => {
		setCompletedLocally(true);
		await prefs.setItem(ONBOARDING_COMPLETED_KEY, "true");
	}, [prefs]);

	const savePendingOnboarding = useCallback(
		async (payload: OnboardingPendingPayload) => {
			await prefs.setItem(ONBOARDING_PENDING_KEY, JSON.stringify(payload));
		},
		[prefs],
	);

	const isOnboardingEnabled =
		onboardingEnabled && import.meta.env.VITE_CAPACITOR_BUILD === "true";

	return {
		isOnboardingEnabled,
		isCompleted: completedLocally,
		isLoading: completedLocally === null,
		needsOnboarding: isOnboardingEnabled && completedLocally === false,
		markCompleted,
		savePendingOnboarding,
	};
}
